import React from 'react';
import { Bug, ShieldCheck, Camera, Languages, Lock, Trash2, Settings, Bot, Shirt, Users, Plus, X, Film } from 'lucide-react';
import { useDanceStore } from '../../store/useDanceStore';
import { useStorageStore } from '../../store/useStorageStore';
import { useAuthStore } from '../../store/useAuthStore';
import { ExplorerView } from './ExplorerView';
import { useTranslation } from 'react-i18next';
import { trackEvent } from '../../lib/analytics';
import { fetchWithAuth } from '../../lib/api';
import { Contact } from '../../types';


export const SettingsModule: React.FC = () => {
  const { t, i18n } = useTranslation();
  const [shouldCrash, setShouldCrash] = React.useState(false);
  const [activeTab, setActiveTab] = React.useState<'general' | 'developer'>('general');
  const [developerUnlocked, setDeveloperUnlocked] = React.useState(false);

  // Contacts state
  const [contacts, setContacts] = React.useState<Contact[]>([]);
  const [newContactEmail, setNewContactEmail] = React.useState('');
  const [addingContact, setAddingContact] = React.useState(false);
  const [contactsError, setContactsError] = React.useState<string | null>(null);

  const showCameraDebug = useDanceStore(state => state.showCameraDebug);
  const setShowCameraDebug = useDanceStore(state => state.setShowCameraDebug);
  const showAiFeatures = useStorageStore(state => state.preferences.showAiFeatures);
  const clothingEnabled = useStorageStore(state => state.preferences.clothingEnabled);
  const mannequinGender = useStorageStore(state => state.preferences.mannequinGender);
  const updatePreference = useStorageStore(state => state.updatePreference);
  const openInputDialog = useDanceStore(state => state.openInputDialog);
  const resetAnonymousConsent = useStorageStore(state => state.resetAnonymousConsent);
  const makeConsentStale = useStorageStore(state => state.makeConsentStale);

  const confirmAction = useDanceStore(state => state.confirmAction);

  const user = useAuthStore(state => state.user);
  const acceptedTouAt = useAuthStore(state => state.acceptedTouAt);
  const acceptedPrivacyAt = useAuthStore(state => state.acceptedPrivacyAt);
  const tier = useAuthStore(state => state.tier);
  const updateTier = useAuthStore(state => state.updateTier);

  const anonymousTouAcceptedAt = useStorageStore(state => state.anonymousTouAcceptedAt);
  const anonymousPrivacyAcceptedAt = useStorageStore(state => state.anonymousPrivacyAcceptedAt);

  const effectiveTouAt = user ? acceptedTouAt : anonymousTouAcceptedAt;
  const effectivePrivacyAt = user ? acceptedPrivacyAt : anonymousPrivacyAcceptedAt;

  const loadContacts = React.useCallback(async () => {
    if (!user) return;
    try {
      const data = await fetchWithAuth('/api/contacts');
      if (Array.isArray(data)) {
        setContacts(data);
      }
    } catch (err: any) {
      console.error('Failed to load contacts:', err);
    }
  }, [user]);

  React.useEffect(() => {
    loadContacts();
  }, [loadContacts]);

  const handleAddContact = async (e: React.FormEvent) => {
    e.preventDefault();
    setContactsError(null);
    const trimmed = newContactEmail.trim().toLowerCase();
    if (!trimmed) return;
    const re = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    if (!re.test(trimmed)) {
      setContactsError(t('sharing.invalid_email', 'Please enter a valid email address.'));
      return;
    }
    setAddingContact(true);
    try {
      await fetchWithAuth('/api/contacts', {
        method: 'POST',
        body: JSON.stringify({ contactEmail: trimmed }),
      });
      setNewContactEmail('');
      await loadContacts();
      trackEvent('settings_contact_added', { email: trimmed });
    } catch (err: any) {
      setContactsError(err.message || 'Failed to add contact');
    } finally {
      setAddingContact(false);
    }
  };

  const handleDeleteContact = async (contactId: string, email: string) => {
    let affectedSequences: Array<{ id: string; name: string }> = [];
    try {
      const data = await fetchWithAuth(`/api/contacts/${contactId}/affected-sequences`);
      if (data && Array.isArray(data.sequences)) {
        affectedSequences = data.sequences;
      }
    } catch (e) {
      console.warn('Failed to fetch affected sequences for contact delete:', e);
    }

    const count = affectedSequences.length;

    let messageNode: React.ReactNode;
    if (count === 0) {
      messageNode = t('settings.delete_contact_confirm_message', {
        defaultValue: 'Are you sure you want to remove {{email}} from your contacts list?',
        email,
      });
    } else {
      const prefix = t('settings.delete_contact_confirm_with_sequences', {
        defaultValue: 'Removing {{email}} will also revoke their collaborator access to ',
        email,
      });
      const badgeText = count === 1
        ? t('settings.affected_sequences_badge_singular', { defaultValue: '1 sequence' })
        : t('settings.affected_sequences_badge_plural', { defaultValue: '{{count}} sequences', count });
      const suffix = t('settings.delete_contact_confirm_suffix', {
        defaultValue: '. Are you sure you want to remove this contact?',
      });
      const popoverTitle = t('settings.affected_sequences_popover_title', {
        defaultValue: 'Affected Sequences',
      });

      messageNode = (
        <div>
          <span>{prefix}</span>
          <span className="affected-sequences-trigger">
            {badgeText}
            <div className="affected-sequences-popover" onClick={(e) => e.stopPropagation()}>
              <div className="affected-sequences-popover-header">
                <Film size={12} className="affected-sequences-popover-icon" />
                <span>{popoverTitle} ({count})</span>
              </div>
              <ul className="affected-sequences-popover-list">
                {affectedSequences.map((seq) => (
                  <li key={seq.id} className="affected-sequences-popover-item" title={seq.name}>
                    <Film size={12} className="affected-sequences-popover-icon" />
                    <span>{seq.name}</span>
                  </li>
                ))}
              </ul>
            </div>
          </span>
          <span>{suffix}</span>
        </div>
      );
    }

    confirmAction({
      title: t('settings.delete_contact_confirm_title', { defaultValue: 'Remove Contact?' }),
      message: messageNode,
      confirmLabel: t('common.delete', { defaultValue: 'Delete' }),
      cancelLabel: t('common.cancel'),
      onConfirm: async () => {
        try {
          const res = await fetchWithAuth(`/api/contacts/${contactId}`, { method: 'DELETE' });
          await loadContacts();
          if (res?.unsharedSequenceIds && Array.isArray(res.unsharedSequenceIds)) {
            const currentSequences = useStorageStore.getState().sequences;
            const updated = currentSequences.map((seq) => {
              if (res.unsharedSequenceIds.includes(seq.id) && seq.collaborators) {
                return {
                  ...seq,
                  collaborators: seq.collaborators.filter((c: string) => c !== email),
                };
              }
              return seq;
            });
            useStorageStore.setState({ sequences: updated });
          }
          trackEvent('settings_contact_deleted', {
            contactId,
            unsharedSequenceCount: res?.unsharedSequenceCount || 0,
          });
        } catch (err: any) {
          useDanceStore.getState().setToastMessage(err.message || 'Failed to delete contact', 'error');
        }
      },
    });
  };

  const formatDateTime = (isoString: string | null) => {
    if (!isoString) return '';
    try {
      const date = new Date(isoString);
      if (isNaN(date.getTime())) return isoString;
      return date.toLocaleDateString(i18n.language, {
        dateStyle: 'medium'
      });
    } catch (e) {
      return isoString;
    }
  };

  const handleTabClick = (tab: 'general' | 'developer') => {
    if (tab === 'developer') {
      if (developerUnlocked) {
        setActiveTab('developer');
      } else {
        openInputDialog({
          title: t('settings.developer_password_title', { defaultValue: 'Enter Developer Password' }),
          defaultName: '',
          placeholder: t('settings.password_placeholder', { defaultValue: 'Password' }),
          isPassword: true,
          onSave: (val) => {
            if (val === '') {
              setDeveloperUnlocked(true);
              setActiveTab('developer');
            } else {
              confirmAction({
                title: t('settings.incorrect_password_title', { defaultValue: 'Access Denied' }),
                message: t('settings.incorrect_password_message', { defaultValue: 'The password you entered is incorrect.' }),
                confirmLabel: t('common.ok', { defaultValue: 'OK' }),
                isAlert: true,
                onConfirm: () => {}
              });
            }
          }
        });
      }
    } else {
      setActiveTab('general');
    }
  };

  const changeLanguage = (lng: string) => {
    if (i18n.language === lng) return;
    
    confirmAction({
      title: t('settings.language_reload_title', { defaultValue: 'Reload Required' }),
      message: t('settings.language_reload_message', { defaultValue: 'Changing the language requires an application reload to update all content, including tutorials. Do you want to reload now?' }),
      confirmLabel: t('settings.reload_now'),
      cancelLabel: t('common.cancel'),
      onConfirm: async () => {
        trackEvent('settings_language_changed', { language: lng });
        await useStorageStore.getState().updatePreference('language', lng);
        i18n.changeLanguage(lng).then(() => {
          window.location.reload();
        });
      }
    });
  };


  if (shouldCrash) {
    throw new Error("Sentry triggered app crash");
  }

  const content = (
    <div style={{ maxWidth: 480 }}>
      {activeTab === 'general' && (
        <>
          {/* Language Section */}
          <div className="glass-panel" style={{ padding: '10px 14px', background: 'rgba(30, 41, 59, 0.5)', marginBottom: 8 }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <Languages size={15} color="#a855f7" />
                <span style={{ fontSize: '0.85rem', color: '#fff', fontWeight: 500 }}>{t('settings.language')}</span>
              </div>
              <select
                value={i18n.language}
                onChange={(e) => changeLanguage(e.target.value)}
                style={{
                  background: 'rgba(56, 189, 248, 0.08)',
                  border: '1px solid rgba(56, 189, 248, 0.25)',
                  borderRadius: '6px',
                  color: '#38bdf8',
                  fontSize: '0.78rem',
                  fontWeight: 600,
                  padding: '4px 8px',
                  cursor: 'pointer',
                  outline: 'none',
                  appearance: 'auto',
                }}
              >
                <option value="en" style={{ background: '#1e293b', color: '#fff' }}>English</option>
                <option value="es" style={{ background: '#1e293b', color: '#fff' }}>Español</option>
              </select>
            </div>
          </div>

          {/* Contacts Management Section (Only for authenticated users) */}
          {user && (
            <div className="glass-panel" style={{ padding: '10px 14px', background: 'rgba(30, 41, 59, 0.5)', marginBottom: 8 }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <Users size={15} color="#38bdf8" />
                  <span style={{ fontSize: '0.85rem', color: '#fff', fontWeight: 500 }}>
                    {t('settings.contacts_title', { defaultValue: 'Contacts Manager' })}
                  </span>
                </div>
                <span style={{ fontSize: '0.7rem', color: 'rgba(255,255,255,0.4)', fontWeight: 600 }}>
                  ({contacts.length})
                </span>
              </div>

              {/* Add contact input form */}
              <form onSubmit={handleAddContact} style={{ display: 'flex', gap: 6, marginBottom: 8 }}>
                <input
                  type="email"
                  value={newContactEmail}
                  onChange={(e) => setNewContactEmail(e.target.value)}
                  placeholder={t('sharing.placeholder', { defaultValue: 'Add collaborator by email...' })}
                  disabled={addingContact}
                  style={{
                    flex: 1,
                    background: 'rgba(255, 255, 255, 0.05)',
                    border: '1px solid rgba(255, 255, 255, 0.15)',
                    borderRadius: '6px',
                    padding: '4px 8px',
                    color: '#fff',
                    fontSize: '0.78rem',
                    outline: 'none',
                  }}
                />
                <button
                  type="submit"
                  disabled={addingContact || !newContactEmail.trim()}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 4,
                    padding: '4px 10px',
                    borderRadius: '6px',
                    background: '#38bdf8',
                    border: 'none',
                    color: '#0f172a',
                    fontSize: '0.78rem',
                    fontWeight: 600,
                    cursor: 'pointer',
                    opacity: addingContact || !newContactEmail.trim() ? 0.5 : 1,
                  }}
                >
                  <Plus size={12} />
                  {t('common.add', { defaultValue: 'Add' })}
                </button>
              </form>

              {contactsError && (
                <div className="share-error-message" style={{ marginBottom: 8 }}>{contactsError}</div>
              )}

              {/* Contacts Pill List */}
              {contacts.length === 0 ? (
                <div style={{ fontSize: '0.78rem', color: 'rgba(255,255,255,0.35)', fontStyle: 'italic', padding: '4px 0' }}>
                  {t('settings.no_contacts', { defaultValue: 'No contacts saved yet. Share sequences to automatically build your list.' })}
                </div>
              ) : (
                <div className="share-pills" style={{ maxHeight: 180, overflowY: 'auto', padding: '4px 0' }}>
                  {contacts.map((c) => (
                    <div
                      key={c.id}
                      className={`share-pill ${c.isRegistered ? 'green' : 'red'}`}
                      title={c.isRegistered
                        ? t('sharing.registered_user', { defaultValue: 'Registered User' })
                        : t('sharing.unregistered_user', { defaultValue: 'Unregistered User (Will receive email invite)' })
                      }
                    >
                      <span>{c.contactNickname ? `${c.contactNickname} (${c.contactEmail})` : c.contactEmail}</span>
                      <button
                        type="button"
                        className="share-pill-remove"
                        onClick={() => handleDeleteContact(c.id, c.contactEmail)}
                        title={t('common.delete', { defaultValue: 'Delete' })}
                      >
                        <X size={10} />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Privacy & Onboarding Section */}
          <div className="glass-panel" style={{ padding: '10px 14px', background: 'rgba(30, 41, 59, 0.5)' }}>
            <div style={{ fontSize: '0.75rem', fontWeight: 600, color: 'rgba(255,255,255,0.4)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 8 }}>
              {t('settings.privacy_onboarding_title', { defaultValue: 'Privacy & Onboarding' })}
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              {/* Terms of Use row */}
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '7px 0' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, flex: 1, minWidth: 0 }}>
                  <a href="/termsofuse" target="_blank" rel="noopener noreferrer" style={{ fontSize: '0.85rem', color: '#38bdf8', fontWeight: 500, flexShrink: 0, textDecoration: 'none' }}
                    onMouseEnter={(e) => { (e.currentTarget as HTMLAnchorElement).style.textDecoration = 'underline'; }}
                    onMouseLeave={(e) => { (e.currentTarget as HTMLAnchorElement).style.textDecoration = 'none'; }}
                  >
                    {t('settings.tou_label', { defaultValue: 'Terms of Use' })}
                  </a>
                  <span style={{
                    fontSize: '0.65rem',
                    color: effectiveTouAt ? '#4ade80' : '#f87171',
                    background: effectiveTouAt ? 'rgba(74, 222, 128, 0.1)' : 'rgba(248, 113, 113, 0.1)',
                    padding: '1px 6px',
                    borderRadius: 3,
                    fontWeight: 600,
                    flexShrink: 0
                  }}>
                    {effectiveTouAt ? t('settings.accepted', { defaultValue: 'Accepted' }) : t('settings.not_accepted', { defaultValue: 'Not Accepted' })}
                  </span>
                  {effectiveTouAt && (
                    <span style={{ fontSize: '0.7rem', color: 'rgba(255,255,255,0.3)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {formatDateTime(effectiveTouAt)}
                    </span>
                  )}
                </div>
              </div>

              <div style={{ height: '1px', background: 'rgba(255,255,255,0.05)' }} />

              {/* Privacy Policy row */}
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '7px 0' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, flex: 1, minWidth: 0 }}>
                  <a href="/privacy" target="_blank" rel="noopener noreferrer" style={{ fontSize: '0.85rem', color: '#38bdf8', fontWeight: 500, flexShrink: 0, textDecoration: 'none' }}
                    onMouseEnter={(e) => { (e.currentTarget as HTMLAnchorElement).style.textDecoration = 'underline'; }}
                    onMouseLeave={(e) => { (e.currentTarget as HTMLAnchorElement).style.textDecoration = 'none'; }}
                  >
                    {t('settings.privacy_label', { defaultValue: 'Privacy Policy' })}
                  </a>
                  <span style={{
                    fontSize: '0.65rem',
                    color: effectivePrivacyAt ? '#4ade80' : '#f87171',
                    background: effectivePrivacyAt ? 'rgba(74, 222, 128, 0.1)' : 'rgba(248, 113, 113, 0.1)',
                    padding: '1px 6px',
                    borderRadius: 3,
                    fontWeight: 600,
                    flexShrink: 0
                  }}>
                    {effectivePrivacyAt ? t('settings.accepted', { defaultValue: 'Accepted' }) : t('settings.not_accepted', { defaultValue: 'Not Accepted' })}
                  </span>
                  {effectivePrivacyAt && (
                    <span style={{ fontSize: '0.7rem', color: 'rgba(255,255,255,0.3)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {formatDateTime(effectivePrivacyAt)}
                    </span>
                  )}
                </div>
              </div>

              <div style={{ height: '1px', background: 'rgba(255,255,255,0.05)', marginBottom: 8 }} />

              {/* Withdraw Button */}
              <button
                onClick={() => {
                  confirmAction({
                    title: t('settings.withdraw_confirm_title', { defaultValue: 'Withdraw Consent?' }),
                    message: t('settings.withdraw_confirm_message', { defaultValue: 'Are you sure you want to withdraw your consent to our Terms of Use and Privacy Policy? This will sign you out and reset your local profile. You will need to accept the policies again to use the application.' }),
                    confirmLabel: t('settings.withdraw_consent', { defaultValue: 'Withdraw Consent' }),
                    cancelLabel: t('common.cancel'),
                    onConfirm: async () => {
                      trackEvent('settings_consent_withdrawn');
                      resetAnonymousConsent();
                      const authState = useAuthStore.getState();
                      if (authState.user) {
                        await authState.logout();
                      }
                      window.location.reload();
                    }
                  });
                }}
                style={{
                  padding: '6px 12px',
                  borderRadius: '6px',
                  background: 'rgba(239, 68, 68, 0.08)',
                  border: '1px solid rgba(239, 68, 68, 0.2)',
                  color: '#ef4444',
                  cursor: 'pointer',
                  fontSize: '0.78rem',
                  fontWeight: 600,
                  transition: 'all 0.2s',
                  overflow: 'hidden',
                  width: 'fit-content',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = 'rgba(239, 68, 68, 0.18)';
                  e.currentTarget.style.borderColor = 'rgba(239, 68, 68, 0.4)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = 'rgba(239, 68, 68, 0.08)';
                  e.currentTarget.style.borderColor = 'rgba(239, 68, 68, 0.2)';
                }}
              >
                {t('settings.withdraw_consent', { defaultValue: 'Withdraw Consent' })}
              </button>
            </div>
          </div>
          {/* Developer access button */}
          <button
            onClick={() => handleTabClick('developer')}
            title={t('settings.developer')}
            style={{
              marginTop: 12,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: 28,
              height: 28,
              borderRadius: '6px',
              background: 'transparent',
              border: '1px solid rgba(255,255,255,0.08)',
              color: 'rgba(255,255,255,0.25)',
              cursor: 'pointer',
              padding: 0,
              transition: 'all 0.2s',
              overflow: 'hidden',
            }}
            onMouseEnter={(e) => { e.currentTarget.style.borderColor = 'rgba(255,255,255,0.2)'; e.currentTarget.style.color = 'rgba(255,255,255,0.5)'; }}
            onMouseLeave={(e) => { e.currentTarget.style.borderColor = 'rgba(255,255,255,0.08)'; e.currentTarget.style.color = 'rgba(255,255,255,0.25)'; }}
          >
            <Lock size={12} />
          </button>
        </>

      )}


      {activeTab === 'developer' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          {/* Mannequin Style & Wardrobe Section */}
          <div className="glass-panel" style={{ padding: '10px 14px', background: 'rgba(30, 41, 59, 0.5)' }}>
            <div style={{ fontSize: '0.75rem', fontWeight: 600, color: 'rgba(255,255,255,0.4)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 8 }}>
              {t('settings.wardrobe_title', { defaultValue: 'Mannequin Style & Wardrobe' })}
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              {/* Show Clothing Toggle */}
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: '0.85rem', color: '#fff', fontWeight: 500 }}>
                  <Shirt size={15} color="#38bdf8" />
                  <span>{t('settings.show_clothing', { defaultValue: 'Show Clothing' })}</span>
                </div>
                <button
                  id="toggle-show-clothing"
                  onClick={() => {
                    const enabled = !clothingEnabled;
                    trackEvent('settings_clothing_toggled', { enabled });
                    updatePreference('clothingEnabled', enabled);
                  }}
                  style={{
                    width: 36, height: 18, borderRadius: 9,
                    background: clothingEnabled ? '#38bdf8' : 'rgba(255,255,255,0.1)',
                    border: 'none', position: 'relative', cursor: 'pointer',
                    transition: 'background 0.2s', padding: 0, flexShrink: 0
                  }}
                >
                  <div style={{
                    position: 'absolute', top: 2,
                    left: clothingEnabled ? 19 : 2,
                    width: 14, height: 14, borderRadius: '50%',
                    background: 'white', transition: 'left 0.2s'
                  }} />
                </button>
              </div>

              {clothingEnabled && (
                <>
                  <div style={{ height: '1px', background: 'rgba(255,255,255,0.05)' }} />

                  {/* Mannequin Gender Selector */}
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <div style={{ fontSize: '0.85rem', color: '#fff', fontWeight: 500 }}>
                      {t('settings.mannequin_style', { defaultValue: 'Mannequin Style' })}
                    </div>
                    <select
                      id="select-mannequin-gender"
                      value={mannequinGender}
                      onChange={(e) => {
                        const gender = e.target.value as 'male' | 'female';
                        trackEvent('settings_clothing_gender_changed', { gender });
                        updatePreference('mannequinGender', gender);
                      }}
                      style={{
                        background: 'rgba(56, 189, 248, 0.08)',
                        border: '1px solid rgba(56, 189, 248, 0.25)',
                        borderRadius: '6px',
                        color: '#38bdf8',
                        fontSize: '0.78rem',
                        fontWeight: 600,
                        padding: '4px 8px',
                        cursor: 'pointer',
                        outline: 'none',
                        appearance: 'auto',
                      }}
                    >
                      <option value="male" style={{ background: '#1e293b', color: '#fff' }}>
                        {t('settings.gender_male', { defaultValue: 'Male' })}
                      </option>
                      <option value="female" style={{ background: '#1e293b', color: '#fff' }}>
                        {t('settings.gender_female', { defaultValue: 'Female' })}
                      </option>
                    </select>
                  </div>
                </>
              )}
            </div>
          </div>

          {/* Camera Debug toggle */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: '0.8rem', color: '#cbd5e1' }}>
              <Camera size={14} />
              {t('settings.camera_debug')}
            </div>
            <button
              onClick={() => {
                const enabled = !showCameraDebug;
                trackEvent('settings_camera_debug_toggled', { enabled });
                setShowCameraDebug(enabled);
              }}
              style={{
                width: 36, height: 18, borderRadius: 9,
                background: showCameraDebug ? '#38bdf8' : 'rgba(255,255,255,0.1)',
                border: 'none', position: 'relative', cursor: 'pointer',
                transition: 'background 0.2s', padding: 0, flexShrink: 0
              }}
            >
              <div style={{
                position: 'absolute', top: 2,
                left: showCameraDebug ? 19 : 2,
                width: 14, height: 14, borderRadius: '50%',
                background: 'white', transition: 'left 0.2s'
              }} />
            </button>
          </div>

          {/* AI Features toggle */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: '0.8rem', color: '#cbd5e1' }}>
              <Bot size={14} />
              {t('settings.ai_features')}
            </div>
            <button
              onClick={() => {
                const enabled = !showAiFeatures;
                trackEvent('settings_ai_features_toggled', { enabled });
                updatePreference('showAiFeatures', enabled);
              }}
              style={{
                width: 36, height: 18, borderRadius: 9,
                background: showAiFeatures ? '#c084fc' : 'rgba(255,255,255,0.1)',
                border: 'none', position: 'relative', cursor: 'pointer',
                transition: 'background 0.2s', padding: 0, flexShrink: 0
              }}
            >
              <div style={{
                position: 'absolute', top: 2,
                left: showAiFeatures ? 19 : 2,
                width: 14, height: 14, borderRadius: '50%',
                background: 'white', transition: 'left 0.2s'
              }} />
            </button>
          </div>

          {/* Dev Plan Tier Selector */}
          {user && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: '0.8rem', color: '#cbd5e1' }}>
                <ShieldCheck size={14} />
                <span>{t('profile.plan', { defaultValue: 'Plan' })} (Mock)</span>
              </div>
              <select
                value={tier}
                onChange={async (e) => {
                  const nextTier = e.target.value as any;
                  try {
                    await updateTier(nextTier);
                    useDanceStore.getState().setToastMessage(t('profile.tier_updated', 'Plan updated successfully (Dev Mode)'), 'info');
                  } catch (err: any) {
                    useDanceStore.getState().setToastMessage(err.message || 'Failed to update plan', 'error');
                  }
                }}
                style={{
                  background: 'rgba(56, 189, 248, 0.08)',
                  border: '1px solid rgba(56, 189, 248, 0.25)',
                  borderRadius: '6px',
                  color: '#38bdf8',
                  fontSize: '0.78rem',
                  fontWeight: 600,
                  padding: '4px 8px',
                  cursor: 'pointer',
                  outline: 'none',
                  appearance: 'auto',
                }}
              >
                <option value="registered" style={{ background: '#1e293b', color: '#fff' }}>Registered</option>
                <option value="basic" style={{ background: '#1e293b', color: '#fff' }}>Basic</option>
                <option value="pro" style={{ background: '#1e293b', color: '#fff' }}>Pro</option>
              </select>
            </div>
          )}

          {/* Action buttons row */}
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <button
              onClick={() => { trackEvent('settings_crash_triggered'); setShouldCrash(true); }}
              style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '5px 10px', cursor: 'pointer', borderRadius: 6, fontSize: '0.75rem', color: '#ef4444', background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.2)', transition: 'all 0.2s' }}
              onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(239,68,68,0.2)'; e.currentTarget.style.borderColor = 'rgba(239,68,68,0.4)'; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = 'rgba(239,68,68,0.1)'; e.currentTarget.style.borderColor = 'rgba(239,68,68,0.2)'; }}
            >
              <Bug size={13} /> {t('settings.crash_studio')}
            </button>

            <button
              onClick={() => { trackEvent('settings_consent_reset'); resetAnonymousConsent(); }}
              style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '5px 10px', cursor: 'pointer', borderRadius: 6, fontSize: '0.75rem', color: '#38bdf8', background: 'rgba(56,189,248,0.1)', border: '1px solid rgba(56,189,248,0.2)', transition: 'all 0.2s' }}
              onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(56,189,248,0.2)'; e.currentTarget.style.borderColor = 'rgba(56,189,248,0.4)'; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = 'rgba(56,189,248,0.1)'; e.currentTarget.style.borderColor = 'rgba(56,189,248,0.2)'; }}
            >
              <ShieldCheck size={13} /> {t('settings.reset_consent')}
            </button>

            <button
              onClick={() => {
                trackEvent('settings_consent_stale_triggered');
                makeConsentStale();
                useAuthStore.setState({ touUpdateRequired: true, privacyUpdateRequired: true });
              }}
              style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '5px 10px', cursor: 'pointer', borderRadius: 6, fontSize: '0.75rem', color: '#fbbf24', background: 'rgba(251,191,36,0.1)', border: '1px solid rgba(251,191,36,0.2)', transition: 'all 0.2s' }}
              onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(251,191,36,0.2)'; e.currentTarget.style.borderColor = 'rgba(251,191,36,0.4)'; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = 'rgba(251,191,36,0.1)'; e.currentTarget.style.borderColor = 'rgba(251,191,36,0.2)'; }}
            >
              <ShieldCheck size={13} /> {t('settings.make_consent_stale')}
            </button>

            <button
              onClick={() => {
                confirmAction({
                  title: t('settings.clear_all_data_confirm_title'),
                  message: t('settings.clear_all_data_confirm_message'),
                  confirmLabel: t('settings.clear_all_data_btn'),
                  cancelLabel: t('common.cancel'),
                  onConfirm: async () => {
                    trackEvent('settings_clear_all_data_triggered');
                    localStorage.clear();
                    sessionStorage.clear();
                    const authState = useAuthStore.getState();
                    if (authState.user) { await authState.logout(); }
                    window.location.reload();
                  }
                });
              }}
              style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '5px 10px', cursor: 'pointer', borderRadius: 6, fontSize: '0.75rem', color: '#ef4444', background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.2)', transition: 'all 0.2s' }}
              onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(239,68,68,0.2)'; e.currentTarget.style.borderColor = 'rgba(239,68,68,0.4)'; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = 'rgba(239,68,68,0.1)'; e.currentTarget.style.borderColor = 'rgba(239,68,68,0.2)'; }}
            >
              <Trash2 size={13} /> {t('settings.clear_all_data_btn')}
            </button>
          </div>
        </div>
      )}
    </div>
  );

  const titleNode = (
    <div className="explorer-header-title-container">
      <div className="module-icon-box" style={{ background: 'rgba(56, 189, 248, 0.15)', color: '#38bdf8', padding: '8px', borderRadius: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
        <Settings size={24} />
      </div>
      <div>
        <h2 style={{ margin: 0, fontWeight: 600, letterSpacing: '-0.02em', color: '#38bdf8' }}>
          {t('settings.title', { defaultValue: 'Settings' })}
        </h2>
        <p className="module-header-subtitle" style={{ margin: 0, fontSize: '0.8rem', color: 'rgba(255,255,255,0.45)' }}>{t('settings.subtitle', { defaultValue: 'Customize your workspace' })}</p>
      </div>
    </div>
  );

  return <ExplorerView title={titleNode} content={content} />;
};
